import * as React from "react";
import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Text,
  Card,
  Input,
  Field,
  Dropdown,
  Option,
  Caption1,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Dismiss24Regular,
  ArrowLeft24Regular,
  DocumentTable24Regular,
  DocumentPdf24Regular,
  Attach24Regular,
  Document24Regular,
  Delete20Regular,
} from "@fluentui/react-icons";
import { createStatusItems, StatusItemDto } from "../../../services/status-items.service";
import { useTrackEvent } from "../../../hooks/useTrackEvent";
// Document linking is now done via the document picker after creation
import { ExcelService } from "../../../services/excel.service";
import { isStandaloneMode } from "../../../utils/standalone-mode";
import { getUserFriendlyErrorMessage } from "../../../utils/error-messages";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: "500px",
    minWidth: "400px",
  },
  typeSelector: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalL,
  },
  card: {
    padding: tokens.spacingVerticalL,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: tokens.spacingVerticalS,
    border: `2px solid transparent`,
    transition: "all 0.2s ease",
    ":hover": {
      boxShadow: tokens.shadow8,
      transform: "translateY(-2px)",
    },
  },
  cardSelected: {
    border: `2px solid ${tokens.colorBrandBackground}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "48px",
    height: "48px",
    borderRadius: tokens.borderRadiusCircular,
  },
  selectedTypeHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
  },
  typeIcon: {
    width: "24px",
    height: "24px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  charCount: {
    textAlign: "right",
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalM,
  },
  placeholderBox: {
    padding: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    border: `2px dashed ${tokens.colorNeutralStroke1}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalS,
  },
  placeholderText: {
    textAlign: "center",
  },
  filePickerBox: {
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    border: `2px dashed ${tokens.colorNeutralStroke1}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalS,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      border: `2px dashed ${tokens.colorBrandBackground}`,
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  selectedFileBox: {
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
  },
  fileInfo: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },
  fileName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fileSize: {
    color: tokens.colorNeutralForeground3,
  },
  hiddenInput: {
    display: "none",
  },
});

type StatusItemType = "workflow_item" | "document";

interface StatusItemTypeOption {
  id: StatusItemType;
  name: string;
  description: string;
  icon: React.ComponentType;
  color: string;
}

const STATUS_ITEM_TYPES: StatusItemTypeOption[] = [
  {
    id: "workflow_item",
    name: "Workflow Item",
    description: "Track a worksheet or workpaper item",
    icon: DocumentTable24Regular,
    color: "#0078D4",
  },
  {
    id: "document",
    name: "Document",
    description: "Upload a supporting document",
    icon: DocumentPdf24Regular,
    color: "#D83B01",
  },
];

interface CreateStatusItemDialogProps {
  isOpen: boolean;
  onDismiss: () => void;
  spreadsheetId: string;
  onSuccess: () => void;
  existingStatusItems: StatusItemDto[];
}

/**
 * Modal for creating a status item — name, type (workflow / supporting doc /
 * note), preparer / reviewer / partner assignments, and optional sheet link.
 */
const CreateStatusItemDialog: React.FC<CreateStatusItemDialogProps> = ({
  isOpen,
  onDismiss,
  spreadsheetId,
  onSuccess,
  existingStatusItems,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const track = useTrackEvent();

  // State
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState<StatusItemType | null>(null);
  const [name, setName] = useState("");
  const [selectedSheetId, setSelectedSheetId] = useState<string | null>(null);
  const [worksheets, setWorksheets] = useState<{ id: string; name: string }[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<{ name?: string; file?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Max file size: 50MB
  const MAX_FILE_SIZE = 50 * 1024 * 1024;

  // Load worksheet names when dialog opens. Standalone has no Excel to read
  // from — the "link to sheet" dropdown just shows empty, and users can
  // create a status item that isn't attached to a specific sheet.
  useEffect(() => {
    if (isOpen) {
      if (isStandaloneMode()) {
        setWorksheets([]);
        return;
      }
      const loadWorksheets = async () => {
        try {
          const worksheetInfos = await ExcelService.getAllWorksheetsInfo();
          setWorksheets(worksheetInfos);
        } catch (error) {
          console.error("Failed to load worksheets:", error);
        }
      };
      loadWorksheets();
    }
  }, [isOpen]);

  // Get selected type details
  const selectedTypeDetails = selectedType
    ? STATUS_ITEM_TYPES.find((t) => t.id === selectedType)
    : null;

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: { name?: string; file?: string } = {};

    if (!name.trim()) {
      newErrors.name = "Name is required";
    } else if (name.length > 100) {
      newErrors.name = "Name must be 100 characters or less";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type (PDF only for now)
      if (file.type !== "application/pdf") {
        setErrors({ ...errors, file: "Only PDF files are supported" });
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setErrors({ ...errors, file: "File size must be less than 50MB" });
        return;
      }
      setSelectedFile(file);
      setErrors({ ...errors, file: undefined });
    }
  };

  // Handle file removal
  const handleFileRemove = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Open file picker
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  // Handlers
  const handleTypeSelect = (typeId: StatusItemType) => {
    setSelectedType(typeId);
    setStep(2);
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setErrors({});
      setSubmitError(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedType) return;

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Create the status item first
      const createdItems = await createStatusItems({
        spreadsheetId,
        items: [
          {
            name: name.trim(),
            type: selectedType,
            sheetId: selectedType === "workflow_item" ? selectedSheetId || undefined : undefined,
            status: "Open",
          },
        ],
      });
      track("status_item.created", { type: selectedType });

      // Reset and close
      handleClose();
      onSuccess();
    } catch (error) {
      const friendlyMessage = getUserFriendlyErrorMessage(error);
      setSubmitError(friendlyMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setStep(1);
    setSelectedType(null);
    setName("");
    setSelectedSheetId(null);
    setSelectedFile(null);
    setErrors({});
    setSubmitError(null);
    setIsSubmitting(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onDismiss();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_e, data) => !data.open && handleClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                onClick={handleClose}
              />
            }
          >
            Add Status Item
          </DialogTitle>

          <DialogContent>
            {submitError && (
              <div className={styles.errorMessage}>
                <Text weight="semibold">Error creating status item</Text>
                <br />
                <Text>{submitError}</Text>
              </div>
            )}

            {/* Step 1: Select type */}
            {step === 1 && (
              <div>
                <Text
                  className={typography.sectionTitle}
                  style={{ marginBottom: tokens.spacingVerticalM, display: "block" }}
                >
                  Select Item Type
                </Text>
                <div className={styles.typeSelector}>
                  {STATUS_ITEM_TYPES.map((type) => {
                    const Icon = type.icon;
                    const isSelected = selectedType === type.id;

                    return (
                      <Card
                        key={type.id}
                        className={`${styles.card} ${isSelected ? styles.cardSelected : ""}`}
                        onClick={() => handleTypeSelect(type.id)}
                      >
                        <div
                          className={styles.iconContainer}
                          style={{ backgroundColor: `${type.color}20`, color: type.color }}
                        >
                          <Icon />
                        </div>
                        <Text className={typography.formLabel}>{type.name}</Text>
                        <Text className={typography.caption}>{type.description}</Text>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 2: Form */}
            {step === 2 && selectedTypeDetails && (
              <>
                <div className={styles.selectedTypeHeader}>
                  <div
                    className={styles.typeIcon}
                    style={{ color: selectedTypeDetails.color }}
                  >
                    {React.createElement(selectedTypeDetails.icon)}
                  </div>
                  <Text weight="semibold">{selectedTypeDetails.name}</Text>
                </div>

                <div className={styles.form}>
                  {/* Name field */}
                  <Field
                    label="Name"
                    required
                    validationState={errors.name ? "error" : undefined}
                    validationMessage={errors.name}
                  >
                    <Input
                      value={name}
                      onChange={(_e, data) => setName(data.value)}
                      placeholder="Enter item name"
                      maxLength={100}
                      disabled={isSubmitting}
                    />
                    <Text className={`${typography.caption} ${styles.charCount}`}>{name.length}/100</Text>
                  </Field>

                  {/* Conditional fields based on type */}
                  {selectedType === "workflow_item" && (
                    <Field label="Associated Sheet" hint="Optional - link to a specific worksheet">
                      <Dropdown
                        value={selectedSheetId ? (worksheets.find((ws) => ws.id === selectedSheetId)?.name ?? "") : "None"}
                        selectedOptions={[selectedSheetId || ""]}
                        onOptionSelect={(_e, data) =>
                          setSelectedSheetId(data.optionValue as string || null)
                        }
                        placeholder="Select a sheet (optional)"
                        disabled={isSubmitting}
                      >
                        <Option key="" value="">
                          None
                        </Option>
                        {worksheets
                          .filter((ws) => !existingStatusItems.some((item) => item.sheetId === ws.id))
                          .map((ws) => (
                            <Option key={ws.id} value={ws.id} text={ws.name}>
                              {ws.name}
                            </Option>
                          ))}
                      </Dropdown>
                    </Field>
                  )}

                  {selectedType === "document" && (
                    <Text className={typography.emptyStateBody}>
                      You can link a client document after creation using the attach button.
                    </Text>
                  )}
                </div>
              </>
            )}
          </DialogContent>

          <DialogActions>
            {step === 1 ? (
              <Button appearance="secondary" onClick={handleClose}>
                Cancel
              </Button>
            ) : (
              <>
                <Button
                  appearance="secondary"
                  icon={<ArrowLeft24Regular />}
                  onClick={handleBack}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
                <Button
                  appearance="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create"}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default CreateStatusItemDialog;
